/**
 * Write text to the clipboard, with the fallback the async API needs to be
 * usable in the environments Bower actually runs in.
 *
 * `navigator.clipboard.writeText` is not a reliable single path, and BOTH of
 * its failure modes were live on 2026-08-14 — measured, not theorised:
 *
 * - **Insecure origin.** `navigator.clipboard` is `undefined` outside a secure
 *   context. `pnpm dev` binds Vite with `host: true` and the dev `.env` points
 *   the API at a LAN IP, so the everyday dev origin is `http://<lan-ip>:3000` —
 *   `isSecureContext: false`, no `navigator.clipboard` at all. Every copy button
 *   in the app was dead there.
 * - **Permission denied on a secure origin.** On `https://app.bowerlabs.ai`,
 *   from a real click, `writeText` rejects `NotAllowedError: Write permission
 *   denied`. The Async Clipboard API is gated on more than the origin — the
 *   permission state, and whether the browser considers the document focused,
 *   both deny it. Same click, `document.execCommand('copy')` returned `true`.
 *
 * So the fallback is not legacy-browser courtesy; it is the path that works.
 *
 * Ordering matters: the async API is tried FIRST where it exists, because it is
 * the one that doesn't disturb the user's selection, and `execCommand` only runs
 * when it is missing or refuses. Both branches stay inside the click handler's
 * transient user activation — `execCommand('copy')` returns `false` without it
 * (confirmed: the same snippet returns `false` from a console eval and `true`
 * from a click), which is why callers must not defer this behind their own
 * awaits.
 *
 * Returns whether the text actually reached the clipboard. It never throws and
 * never rejects — a caller that ignores the result silently tells the user
 * nothing, which is the exact failure this replaces.
 */

/**
 * The pre-Async-Clipboard path: select text in an offscreen textarea and let
 * the document copy command take it.
 *
 * The textarea must be RENDERED — `display: none`, `hidden`, or zero size gives
 * an empty selection and copies nothing. `opacity: 0` with
 * `pointer-events: none` keeps it invisible and inert while still selectable.
 * The caller's own selection is restored afterwards so copying a code block
 * doesn't wipe a highlight the user made in their note.
 */
function copyViaExecCommand(text: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // readOnly, not disabled: a disabled control cannot hold a selection.
  textarea.readOnly = true;
  textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  let copied = false;
  try {
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    // Deprecated, and deliberately so — see the module docblock. jsdom doesn't
    // implement it at all, so this throws rather than returning false there.
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);
  if (previousRange && selection) {
    selection.removeAllRanges();
    selection.addRange(previousRange);
  }
  return copied;
}

/** @returns true when the text reached the clipboard. Never throws. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // NotAllowedError and friends — fall through rather than surface a failure
    // the fallback may well be able to avoid.
  }
  return copyViaExecCommand(text);
}
